<script>
import { useDestinationsStore } from '../../stores/destinations.store';
import { useSourcesStore } from '../../stores/sources.store';
import { useMetricsStore } from '../../stores/metrics.store';
import { useRouter } from 'vue-router';
import AppLoader from '../../components/AppLoader.vue';
import { ChevronDown, ChevronUp, ChevronsUpDown, PlusCircle } from 'lucide-vue-next';
import { Info, Clock } from 'lucide-vue-next';
import _ from 'lodash';
import { capitalize } from 'lodash';
import ItemsPerPageSelect from '@/components/filters/ItemsPerPageSelect.vue';
import StatusFilter from '@/components/filters/StatusFilter.vue';
import ConnectorFilter from '@/components/filters/ConnectorFilter.vue';
import { getStatusColor, getBadgeVariantAndClasses, formatter, getMetricFormattingInfo, userHasAccess } from '../../utils/utils';
import { getConnectorIcon } from '@/utils/constants';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableEmpty } from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import TablePagination from '@/components/TablePagination.vue';
import { useDeleteConfirmation, useConfirmBeforeAction } from '@/composables/useDialog';
import { usePipelinesStore } from '../../stores/pipelines.store';
import ActionMenu from '@/components/ActionMenu.vue';
import { TableEmptyState } from '@/components/ui/table-empty-state';
import { useErrorDialog } from '@/composables/useDialog';
import SearchInput from '@/components/global/SearchInput.vue';
import { useAuthStore } from '@/stores/auth.store';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import { toast } from '@/composables/useToast';

export default {
    name: 'DestinationsPage',
    components: {
        ChevronDown,
        ChevronUp,
        ChevronsUpDown,
        Info,
        PlusCircle,
        Clock,
        SkeletonLoader,
        AppLoader,
        ActionMenu,
        TablePagination,
        Table,
        TableBody,
        TableCell,
        TableHead,
        TableHeader,
        TableRow,
        TableEmpty,
        ItemsPerPageSelect,
        SearchInput,
        StatusFilter,
        ConnectorFilter,
        Badge,
        Avatar,
        AvatarImage,
        AvatarFallback,
        Button,
        ScrollArea,
        ScrollBar,
        TableEmptyState
    },
    data() {
        return {
            destinationsStore: useDestinationsStore(),
            sourcesStore: useSourcesStore(),
            router: useRouter(),
            authStore: useAuthStore(),
            metricsStore: useMetricsStore(),
            pipelinesStore: usePipelinesStore(),
            searchQuery: null,
            sortColumn: 'name',
            sortDirection: false,
            triggerFilterSelection: false,
            generalLoading: false,
            metricsLoading: false,
            actionLoading: {},
            destinationsPage: 0,
            destinationsPageSize: 0,
            sourcesPage: 0,
            sourcesPageSize: 0,
            selectedStatusFilters: [],
            selectedConnectorFilters: []
        };
    },
    computed: {
        allLatestMetrics() {
            return this.metricsStore.destinations.allLatest;
        },
        searchedDestinations() {
            let result = this.applyFilters(this.destinationsStore.all);
            result = this.applySorting(result);
            return result;
        },
        possibleConnectors() {
            const uniqueConnectors = new Map();
            this.destinationsStore.all?.forEach((destination) => {
                if (!uniqueConnectors.has(destination.connector)) {
                    uniqueConnectors.set(destination.connector, {
                        id: destination.connector,
                        name: destination.connectorDisplayName,
                        connectorDisplayName: destination.connectorDisplayName,
                        connectorType: destination.connector,
                        context: 'destination'
                    });
                }
            });
            return Array.from(uniqueConnectors.values());
        }
    },
    watch: {
        generalLoading: {
            handler: function (newValue) {
                if (newValue === true) {
                    const mainElement = document.querySelector('main');
                    if (mainElement) {
                        mainElement.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                }
            }
        },
        searchQuery: {
            handler: _.debounce(async function () {
                await this.loadDestinations();
            }, 700)
        }
    },
    mounted() {
        // Reset pagination to first page on mount to prevent persisting page state across navigation
        this.destinationsPage = 0;
        this.destinationsPageSize = this.destinationsStore.pageSize || 10;

        this.sourcesPage = 0;
        this.sourcesPageSize = this.sourcesStore.pageSize || 10;

        this.loadDestinations();
        this.loadSources();
    },
    methods: {
        capitalize,
        userHasAccess,
        getStatusColor,
        getConnectorIcon,
        getStatus(destination) {
            return (
                this.allLatestMetrics[destination.id]?.inline?.connector_status ??
                destination?.connector_status ??
                (destination?.connector === 'kafkadirect' ? 'Active' : 'Unknown')
            );
        },
        getBadgeVariantAndClasses,

        async onDelete(entity, name, id, index) {
            this.actionLoading[id] = true;

            try {
                await useDeleteConfirmation({
                    entity,
                    name,
                    title: `Delete ${entity}`,
                    message: `Are you sure you want to delete the ${entity} <strong>"${name}"</strong>? This action cannot be undone.`,
                    onConfirm: async () => {
                        await this.destinationsStore.delete(id);
                    }
                });
            } catch (error) {
                if (error.message) {
                    console.error('Delete failed:', error);
                }
            } finally {
                this.actionLoading[id] = false;
            }
        },
        async onStop(id) {
            try {
                this.actionLoading[id] = true;
                await this.destinationsStore.stop(id);
            } finally {
                this.actionLoading[id] = false;
            }
        },
        async onResume(id) {
            try {
                this.actionLoading[id] = true;
                await this.destinationsStore.resume(id);
            } finally {
                this.actionLoading[id] = false;
            }
        },
        async onRestart(id) {
            try {
                this.actionLoading[id] = true;
                await this.destinationsStore.restart(id);
                await this.destinationsStore.getById(id);
            } finally {
                this.actionLoading[id] = false;
            }
        },
        async onReset(destination) {
            const message = `Are you sure you want to reset '<strong>${destination.name}</strong>'?<br><br>` +
                `<ul>` +
                `<li>The connector will stop and restart from the beginning of the input topics</li>` +
                `<li>1 week worth of data or more might be replayed</li>` +
                `<li>Default kafka retention in Streamkap is 7 days</li>` +
                `<li>Consumer lag alerts might be raised until the data backlog is replayed</li>` +
                `<li><strong>This action cannot be undone</strong></li>` +
                `</ul>`;

            try {
                await useConfirmBeforeAction('reset', async () => {
                    this.actionLoading[destination.id] = true;
                    await this.destinationsStore.reset(destination.id);
                    this.actionLoading[destination.id] = false;
                }, {
                    message,
                    expectedInput: 'reset'
                });
            } catch (err) {
                console.log('Reset cancelled or failed:', err);
                this.actionLoading[destination.id] = false;
            }
        },
        onClone(destination) {
            this.router.push({
                name: 'new-destination',
                params: { connector: destination.connector },
                query: { cloneConfig: JSON.stringify(destination.config) }
            });
        },
        onViewLogs(destinationId) {
            const route = this.router.resolve({
                name: 'logs',
                query: { destination_id: destinationId }
            });
            window.open(route.href, '_blank');
        },
        getConnectorInfo(connector) {
            return Object.values(this.destinationsStore.destinationConnectors || {})
                .flat()
                .find((c) => c.connector === connector);
        },
        getMetricFormattingInfo,
        formatter,
        getFormattedValue(connectorToSearch, metricKey, dashOnRule = null) {
            const metricValue = this.allLatestMetrics[connectorToSearch.id]?.inline?.[metricKey];

            const connectorInfo = this.getConnectorInfo(connectorToSearch.connector);
            if (!connectorInfo) return { display: '-' };

            const metricFormattingInfo = getMetricFormattingInfo(metricKey, connectorInfo.metrics);

            const formatterParams = [metricValue, metricFormattingInfo || {}];

            if (dashOnRule || dashOnRule === false) {
                formatterParams.push(dashOnRule);
            }

            return this.formatter(...formatterParams);
        },
        onCreateDestination() {
            return this.router.push({ name: 'add-connectors', query: { tab: 'Destinations' } });
        },
        onDestinationSelected(id) {
            return this.router.push(this.selectedDestinationRouterPath(id));
        },
        selectedDestinationRouterPath(id) {
            return { name: 'destination-details', params: { id } };
        },
        async loadDestinations() {
            try {
                if (this.searchQuery || this.searchQuery === '') {
                    this.destinationsPage = 0;
                }

                this.generalLoading = true;
                await this.destinationsStore.fetchCreated(this.destinationsPage, this.destinationsPageSize, this.searchQuery);
                this.generalLoading = false;

                this.metricsLoading = true;
                await Promise.all([
                    this.metricsStore.destinations.fetchAllInlineDestinationMetrics(this.destinationsStore.all?.map((dest) => dest.id)),
                    this.destinationsStore.fetchConnectors()
                ]);
            } finally {
                this.generalLoading = false;
                this.metricsLoading = false;
            }
        },
        async loadSources() {
            try {
                await this.sourcesStore.fetchCreated(this.sourcesPage, this.sourcesPageSize);
                await Promise.all([
                    this.metricsStore.sources.fetchAllInlineSourceMetrics(this.sourcesStore.all?.map((source) => source.id)),
                    this.sourcesStore.fetchConnectors()
                ]);
            } catch (e) {
                console.error('Error in destinations operation:', e);
            }
        },
        swapSorting(column, direction) {
            this.sortColumn = column;
            this.sortDirection = direction;
        },
        applySorting(result) {
            return _.orderBy(result, [this.sortColumn], [this.sortDirection ? 'desc' : 'asc']);
        },
        applyFilters(result) {
            return (
                result?.filter((x) => {
                    const statusMatches = this.selectedStatusFilters.length <= 0 || this.selectedStatusFilters.includes(this.getStatus(x));

                    const connectorMatches = this.selectedConnectorFilters.length <= 0 || this.selectedConnectorFilters.includes(x.connector);

                    return statusMatches && connectorMatches;
                }) || []
            );
        },
        onStatusFilterChange(selectedStatuses) {
            this.selectedStatusFilters = selectedStatuses;
            this.destinationsPage = 0;
            this.loadDestinations();
        },
        onConnectorFilterChange(selectedConnectors) {
            this.selectedConnectorFilters = selectedConnectors;
            this.destinationsPage = 0;
            this.loadDestinations();
        },
        onDestinationsPerPageChange(newSize) {
            this.destinationsPageSize = newSize;
            this.destinationsPage = 0;
            this.loadDestinations();
        },
        onPageChange(newPage) {
            this.destinationsPage = newPage;
            this.loadDestinations();
        },
        onStatusClick(destination) {
            if (this.getStatus(destination) === 'Broken' && destination.task_statuses) {
                const errors = [...new Set(Object.values(destination.task_statuses).map((task) => task.trace))];
                useErrorDialog({
                    errors,
                    width: 65
                });
            }
        },
        async onCopyId(id) {
            try {
                await navigator.clipboard.writeText(id);
                toast.success('ID copied to clipboard');
            } catch (error) {
                console.error('Failed to copy ID to clipboard:', error);
            }
        }
    }
};
</script>

<template>
    <div class="mx-auto w-full">
        <div>
            <!-- Unified Responsive Layout -->
            <div class="space-y-4 mb-6">
                <div class="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                    <!-- Actions Section -->
                    <div class="flex flex-col sm:flex-row gap-3">
                        <Button
                            v-can-access.permissions="[$Permissions.WRITE_DESTINATIONS]"
                            variant="outline"
                            class="gap-2 w-full sm:w-auto"
                            @click="onCreateDestination">
                            <PlusCircle class="h-4 w-4" />
                            Add
                        </Button>
                        <SearchInput
                            :loading="generalLoading"
                            placeholder="Search by name..."
                            @search="(input) => (searchQuery = input)"
                            class="w-full sm:flex-1 md:w-auto" />
                    </div>

                    <!-- Filters Section -->
                    <div class="flex flex-col sm:flex-row md:flex-row gap-3">
                        <StatusFilter
                            :model-value="selectedStatusFilters"
                            :loading="generalLoading"
                            placeholder="Filter by Status"
                            enable-filter-icon
                            :include-all="false"
                            class="w-full sm:w-auto"
                            @update:model-value="onStatusFilterChange" />

                        <ConnectorFilter
                            :model-value="selectedConnectorFilters"
                            :connectors="possibleConnectors"
                            :loading="generalLoading"
                            placeholder="Filter by Connector"
                            enable-filter-icon
                            :include-all-option="false"
                            class="w-full sm:w-auto"
                            @update:model-value="onConnectorFilterChange" />
                    </div>
                </div>
            </div>

            <div class="mt-2">
                <ScrollArea
                    class="w-full border shadow-md rounded-md"
                    :class="
                        (generalLoading && (destinationsPageSize || 10) <= 10) || (!generalLoading && (searchedDestinations?.length || 0) <= 10)
                            ? 'h-auto'
                            : 'h-70-dynamic'
                    ">
                    <Table>
                        <TableHeader class="sticky top-0 bg-background z-10">
                            <TableRow class="bg-muted/50">
                                <TableHead class="first:rounded-tl-md">Name</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Status/State</TableHead>
                                <TableHead>Topics</TableHead>
                                <TableHead>Latency</TableHead>
                                <TableHead>Lag</TableHead>
                                <TableHead class="last:rounded-tr-md"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <!-- Skeleton rows when loading -->
                            <template v-if="generalLoading">
                                <TableRow v-for="n in destinationsPageSize || 10" :key="`skeleton-${n}`">
                                    <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-type" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-status" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-action" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-number" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-number" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-action" :loading="true" /></TableCell>
                                </TableRow>
                            </template>
                            <!-- Actual data rows -->
                            <template v-else>
                                <TableRow v-for="(destination, destIndex) in searchedDestinations" :key="destIndex">
                                    <TableCell>
                                        <Button
                                            variant="link"
                                            class="h-auto p-0 max-w-full text-left justify-start font-medium"
                                            :title="destination.name"
                                            @click="onDestinationSelected(destination.id)">
                                            <span class="truncate max-w-[200px] sm:max-w-[250px] md:max-w-[300px] lg:max-w-[350px] xl:max-w-[400px]">
                                                {{ destination.name }}
                                            </span>
                                        </Button>
                                    </TableCell>
                                    <TableCell>
                                        <div class="flex items-center gap-2">
                                            <Avatar class="h-6 w-6 bg-background dark:bg-slate-200 border border-border rounded-sm shrink-0">
                                                <AvatarImage
                                                    :src="getConnectorIcon(destination.connector) || `/icons/${destination.connector}.svg`"
                                                    :alt="destination.connector"
                                                    class="object-contain p-0.5" />
                                                <AvatarFallback
                                                    class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-sm">
                                                    {{ destination.connector?.charAt(0).toUpperCase() }}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span>{{ destination.connectorDisplayName }}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <SkeletonLoader preset="badge" :loading="metricsLoading">
                                            <Badge
                                                :variant="getBadgeVariantAndClasses(getStatus(destination)).variant"
                                                :class="getBadgeVariantAndClasses(getStatus(destination)).classes"
                                                @click="() => onStatusClick(destination)">
                                                {{ getStatus(destination) }}
                                                <Info v-if="getStatus(destination) === 'Broken'" class="h-4 w-4 ml-1" />
                                            </Badge>
                                        </SkeletonLoader>
                                    </TableCell>
                                    <TableCell>
                                        {{ destination.topics.length }}
                                    </TableCell>
                                    <TableCell
                                        :variant="
                                            metricsLoading || getFormattedValue(destination, 'latency').display === '-' ? 'default' : 'metric-badge'
                                        ">
                                        <SkeletonLoader v-if="metricsLoading" preset="inline-number" :loading="true" />
                                        <template v-else>
                                            {{ getFormattedValue(destination, 'latency').display }}
                                        </template>
                                    </TableCell>
                                    <TableCell
                                        :variant="
                                            metricsLoading || getFormattedValue(destination, 'recordsLag').display === '-'
                                                ? 'default'
                                                : 'metric-badge'
                                        ">
                                        <SkeletonLoader v-if="metricsLoading" preset="inline-number" :loading="true" />
                                        <template v-else>
                                            {{ getFormattedValue(destination, 'recordsLag').display }}
                                        </template>
                                    </TableCell>
                                    <TableCell>
                                        <ActionMenu
                                            variant="menu"
                                            :show-view="true"
                                            :show-delete="userHasAccess([$Permissions.DELETE_DESTINATIONS], authStore.user)"
                                            :show-reset="userHasAccess([$Permissions.TRIGGER_ACTION_DESTINATIONS], authStore.user)"
                                            :show-clone="userHasAccess([$Permissions.WRITE_DESTINATIONS], authStore.user)"
                                            :show-logs="
                                                userHasAccess(
                                                    [$Permissions.READ_SOURCE_METRICS, $Permissions.READ_DESTINATION_METRICS],
                                                    authStore.user
                                                )
                                            "
                                            :show-stop="
                                                destination?.connector !== 'kafkadirect' &&
                                                userHasAccess([$Permissions.TRIGGER_ACTION_DESTINATIONS], authStore.user) &&
                                                !(
                                                    getStatus(destination) === 'Paused' ||
                                                    getStatus(destination) === 'Starting' ||
                                                    getStatus(destination) === 'Stopped'
                                                )
                                            "
                                            :show-resume="
                                                destination?.connector !== 'kafkadirect' &&
                                                userHasAccess([$Permissions.TRIGGER_ACTION_DESTINATIONS], authStore.user) &&
                                                !(
                                                    getStatus(destination) === 'Active' ||
                                                    getStatus(destination) === 'Broken' ||
                                                    getStatus(destination) === 'Starting'
                                                )
                                            "
                                            :show-restart="
                                                userHasAccess([$Permissions.TRIGGER_ACTION_DESTINATIONS], authStore.user) &&
                                                !(
                                                    getStatus(destination) === 'Active' ||
                                                    getStatus(destination) === 'Paused' ||
                                                    getStatus(destination) === 'Stopped'
                                                )
                                            "
                                            :show-copy-id="true"
                                            :loading="actionLoading[destination.id]"
                                            @stop="onStop(destination.id)"
                                            @resume="onResume(destination.id)"
                                            @restart="onRestart(destination.id)"
                                            @reset="onReset(destination)"
                                            @view="onDestinationSelected(destination.id)"
                                            @clone="onClone(destination)"
                                            @delete="onDelete('destination', destination.name, destination.id, destIndex)"
                                            @logs="onViewLogs(destination.id)"
                                            @copyId="onCopyId(destination.id)" />
                                    </TableCell>
                                </TableRow>
                                <TableEmpty
                                    v-if="!generalLoading && destinationsStore.all !== undefined && !searchedDestinations.length"
                                    :colspan="7">
                                    <TableEmptyState />
                                </TableEmpty>
                            </template>
                        </TableBody>
                    </Table>
                    <ScrollBar orientation="horizontal" />
                    <ScrollBar orientation="vertical" />
                </ScrollArea>
                <TablePagination
                    v-if="destinationsStore.destinations?.length || destinationsPage > 0"
                    :current-page="destinationsPage"
                    :page-size="destinationsPageSize"
                    :total-items="destinationsStore.total"
                    :current-page-items-count="destinationsStore.destinations?.length || 0"
                    :filtered-count="searchedDestinations.length !== destinationsStore.destinations?.length ? searchedDestinations.length : null"
                    item-type="destinations"
                    :loading="generalLoading"
                    @page-change="onPageChange"
                    @page-size-change="onDestinationsPerPageChange" />
            </div>
        </div>
    </div>
</template>

<style scoped>
.my-auto.whitespace-normal {
    min-height: unset;
    max-height: none;
}
</style>
